import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

/* Helper for null-safe query params */
const buildQueryConfig = (paramsObj) => ({
  params: paramsObj,
  paramsSerializer: {
    serialize: (params) =>
      Object.entries(params)
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${v === null ? "null" : encodeURIComponent(v)}`
        )
        .join("&"),
  },
});

const AdminDashboardBlocks = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    approved: 0,
    rejected: 0,
    pending: 0,
    total: 0,
    talentCount: 0,
    directorCount: 0,
    paidTalent: 0,
  });

  useEffect(() => {
    fetchAllCounts();
  }, []);

  const fetchAllCounts = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchNoticeCounts(), fetchUserCounts()]);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNoticeCounts = async () => {
    try {
      const res = await api.get(ENDPOINTS.getNoticeStatusCounts());
      const data = res.data?.data || {};
      setCounts((prev) => ({
        ...prev,
        approved: data.approvedCount || 0,
        rejected: data.rejectedCount || 0,
        pending: data.pendingCount || 0,
        total: data.totalNotices || 0,
      }));
    } catch (err) {
      console.error("Error fetching notice status counts:", err);
    }
  };

  const fetchUserCounts = async () => {
    try {
      const rolesResponse = await api.get(ENDPOINTS.getAllRoles());
      const rolesData = rolesResponse.data?.data || [];

      const talentRole = rolesData.find(
        (r) => r.roleName?.toUpperCase() === "USER"
      );
      const directorRole = rolesData.find(
        (r) => r.roleName?.toUpperCase() === "COMPANY"
      );

      let talentCount = 0;
      let directorCount = 0;

      if (talentRole) {
        const res = await api.get(
          ENDPOINTS.getUsersByRole(),
          buildQueryConfig({
            roleId: talentRole.roleId,
            page: 1,
            pageSize: 1,
            email: null,
            status: null,
          })
        );
        talentCount = res.data?.data?.totalItems || 0;
      }

      if (directorRole) {
        const res = await api.get(
          ENDPOINTS.getUsersByRole(),
          buildQueryConfig({
            roleId: directorRole.roleId,
            page: 1,
            pageSize: 1,
            email: null,
            status: null,
          })
        );
        directorCount = res.data?.data?.totalItems || 0;
      }

      setCounts((prev) => ({
        ...prev,
        talentCount,
        directorCount,
        paidTalent: 0,
      }));
    } catch (err) {
      console.error("Error fetching user/system counts:", err);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          {/* --- Notices Block --- */}
          <Card
            variant="outlined"
            sx={{
              flex: "1 1 48%",
              borderColor: "#ddd",
              "&:hover": { boxShadow: 3 },
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 40, mb: 1, color: "text.primary" }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}
                >
                  Notices
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={2}
                divider={<Divider orientation="vertical" flexItem />}
                justifyContent="space-around"
                alignItems="center"
              >
                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Approved
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.approved.toLocaleString()}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Rejected
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.rejected.toLocaleString()}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.pending.toLocaleString()}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.total.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* --- Users Block --- */}
          <Card
            variant="outlined"
            sx={{
              flex: "1 1 48%",
              borderColor: "#ddd",
              "&:hover": { boxShadow: 3 },
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <GroupIcon
                  sx={{ fontSize: 40, mb: 1, color: "text.primary" }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}
                >
                  Users
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={3}
                divider={<Divider orientation="vertical" flexItem />}
                justifyContent="space-around"
                alignItems="center"
              >
                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Talent
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.talentCount.toLocaleString()}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Director
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.directorCount.toLocaleString()}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Paid Talent
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {counts.paidTalent.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default AdminDashboardBlocks;
